import React, { useState, useEffect } from 'react';
import Entity from '../Common/Entity';
import { 
  Box, 
  Grid, 
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
    CreateOrderAction,
  PoBaseProductDetailsAction,
  createBookingOrder
} from './API/Action';
import { useSelector } from 'react-redux';
import { CustomButton, CustomRadio, TextField } from '../../v2/MUI_custom_component';
import GlassModal from '../../v2/MUI_custom_component/Modal/GlassModal';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../../v2/MUI_custom_component/Badge/StatusBadge';
import PODetailsForm from './Components/PODetailsForm';

const CreatePo = ({title}) => {
    const navigate = useNavigate();
    const [productDetails, setProductDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openProductDialog, setOpenProductDialog] = useState(false);
    const [orderItems, setOrderItems] = useState([]);
    const [poDetails, setPODetails] = useState({
        po_number: '',
        quantity: '',
    });
    const [quantityError, setQuantityError] = useState('');
    const [selectedProductId, setSelectedProductId] = useState(null);
    const { selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity, selectedSupplierData } = useSelector(
        (state) => state.entityReducer
    );
    const validateQuantity = (value) => {
        if (!value || value.trim() === '') {
            return 'Quantity is required';
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            return 'Quantity must be a valid number';
        }
        if (numValue <= 0) {
            return 'Quantity must be greater than 0';
        }
        
        if (productDetails?.limits_info) {
            const { min_moq, max_moq } = productDetails.limits_info;
            if (min_moq && numValue < min_moq) {
                return `Quantity must be at least ${min_moq} tons (minimum MOQ)`;
            }
            if (max_moq && numValue > max_moq) {
                return `Quantity cannot exceed ${max_moq} tons (maximum MOQ)`;
            }
        }
        return '';
    };
    
    const handleQuantityChange = (e) => {
        const value = e.target.value;
        // Only allow numeric input (including decimal points)
        const numericValue = value.replace(/[^0-9.]/g, '');
        // Prevent multiple decimal points
        const validValue = numericValue.replace(/(\..*?)\./g, '$1');
        
        setPODetails({...poDetails, quantity: validValue});
        const error = validateQuantity(validValue);
        setQuantityError(error);
    };
    const fetchProducts = async (selectedManufacturer, selectedCompanyEntity, ) => {
        setLoading(true);
        const { data, error, message } = await PoBaseProductDetailsAction(selectedManufacturer, selectedCompanyEntity);
        setLoading(false);
        if (!error) {
            setProductDetails( data );
            // Auto-select first product if only one product available
            if (data?.products && data.products.length === 1) {
                setSelectedProductId(data.products[0].id);
            } else if (data?.products && data.products.length > 1) {
                setSelectedProductId(null); // Reset selection for multiple products
            }
        } else {
            return toast.error(message);
        }
    };
    useEffect(() => {
        if (selectedManufacturer && selectedCompanyEntity) {
            fetchProducts(selectedManufacturer, selectedCompanyEntity);
        }
    }, [selectedManufacturer, selectedCompanyEntity]);
    const handleCreateOrder = async () => {
        setLoading(true);
        const selectedProduct = productDetails?.products?.length === 1
            ? productDetails.products[0]
            : productDetails?.products?.find((product) => product.id === selectedProductId);
            
        if (!selectedProduct) {
            toast.error('Please select a product');
            setLoading(false);
            return;
        }
        
        const productList = [{
              product_id: selectedProduct.id,
              quantity: {
                quantity_in_metric_ton: parseFloat(poDetails.quantity),
                multiplier: selectedProduct.multiplier,
              }
        }];
        
        const orderData = {
            user_po_number: poDetails.po_number,
            seller_id: selectedManufacturer,
            buyer_esi_id: selectedCompanyEntity,
            product_list: productList,
        };

        const {error, message} = await CreateOrderAction(orderData);
        setLoading(false);
        
        if (!error) {
            toast.success('Purchase Order created successfully!');
            setOrderItems([]);
            setPODetails({ po_number: '', quantity: ''});
            setOpenProductDialog(false);
            navigate('/complete-po');
        } else {
            toast.error(message || 'Failed to create purchase order');
        }
    };

  return (
    <>
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6} >
            <Entity
                entityLabel="SAP Code*"
                showEntitySelect={true}
                forceRefresh={true}
                gridSizes={{
                    supplier: { xs: 12, sm: 12 },
                    entity: { xs: 12, sm: 12 }
                }}
            />
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}>
            {productDetails.company_name &&
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="labelD" sx={{ color: '#666'}}>
                      Manufacturer
                    </Typography>
                    <Typography variant="body1">
                      {productDetails.company_name}
                    </Typography>
                </Box>
            }
            {productDetails?.products && productDetails.products.length > 0 &&  (
              <Box sx={{
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
              }}>
                {productDetails.products.map((product, index) => {
                  const isSelected = selectedProductId === product.id;
                  const showRadioButton = productDetails.products.length > 1;
                  
                  return (
                    <Box 
                      key={product.id || index} 
                      sx={{ 
                        background: '#fff',
                        p: 2, 
                        borderRadius: 2, 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 2,
                        border: (productDetails.products.length > 1 && isSelected) ? '2px solid #995A00' : '2px solid #0000',
                        cursor: showRadioButton ? 'pointer' : 'default',
                        position: 'relative',
                        '&:hover': showRadioButton ? {
                          borderColor: '#995A00'
                        } : {}
                      }}
                      onClick={() => {
                        if (showRadioButton) {
                          setSelectedProductId(product.id);
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center', position: 'relative' }}>
                        <Box
                          component="img"
                          src={product.image}
                          alt={product.name}
                          sx={{
                            maxHeight: 200,
                            objectFit: 'contain',
                            width:'32px',
                            height:'32px',
                            borderRadius: 1,
                            border: '1px solid #D9D9D9',
                          }}
                        />
                        <Typography variant="bodyL" sx={{ flex: 1 }}>{product.name}</Typography>
                        {showRadioButton && (
                          <CustomRadio
                            checked={isSelected}
                            onChange={() => setSelectedProductId(product.id)}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                        }}>
                          <Typography variant="labelM" sx={{ color: '#666', minWidth: '100px' }}>
                            Today’s Price
                          </Typography>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}>
                            <Typography variant="p1">
                              {product.todays_price}
                            </Typography>
                            <StatusBadge 
                              variant={"text"} 
                              label='Ex Factory Price'
                              size='medium'
                              color={'#0081F2'}
                            />
                          </Box>
                        </Box>
                        {product.length && (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}>
                            <Typography variant="labelM" sx={{ color: '#666', minWidth: '100px' }}>
                              Length
                            </Typography>
                            <Typography variant="p1">
                              {product.length}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
            </Box>
        </Grid>
        <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', gap: {xs: 0.5, md:1} }}>
            <PODetailsForm
              poDetails={poDetails}
              setPODetails={setPODetails}
              quantityError={quantityError}
              setQuantityError={setQuantityError}
              productDetails={productDetails}
              selectedProductId={selectedProductId}
              validateQuantity={validateQuantity}
              onCreatePO={() => setOpenProductDialog(true)}
              title={title}
            />
        </Grid>
      </Grid>
    </Box>
    <GlassModal
      open={openProductDialog}
      onClose={() => setOpenProductDialog(false)}
      title = 'Confirm Details'
      scrollable={true}
      maxWidth="600px"
      zIndex={2}
      maxHeight = '45%'
      topMobile = '45%'
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap:{ xs:2, md: 4 }, mb: {xs: 4, sm: 0} }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="labelM" sx={{ color: '#666' }}>
              SAP Code*
            </Typography>
            <Typography variant="Body1">
              {selectedSupplierData && selectedSupplierData.entity_info[0].external_system_code }
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="labelM" sx={{ color: '#666' }}>
              Quantity*
            </Typography>
            <Typography variant="Body1">
              {poDetails.quantity ? `${poDetails.quantity} Ton` : '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="labelM" sx={{ color: '#666' }}>
              User PO No.
            </Typography>
            <Typography variant="Body1">
              { poDetails.po_number || '-'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1}}>
          <Typography variant="h2">
            Are you sure you want to proceed?
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Your purchase order will be created and sent to manufacturer for approval.
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 1,
        }}>
          <CustomButton
            label="No"
            variant="outlined"
            color="primary"
            onClick={() => setOpenProductDialog(false)}
            sx={{ 
              width:'50%',
            }}
          />
          <CustomButton
            label="Yes"
            variant="filled"
            color="primary"
            loading={loading}
            onClick={() => handleCreateOrder()}
            sx={{ 
              width:'50%',
            }}
          />
        </Box>
      </Box>
    </GlassModal>
    </>
  );
};

export default CreatePo;